import { BaseAppDialog } from 'client/modules/app/dialogs/BaseAppDialog';
import { useDialog } from 'client/modules/app/dialogs/hooks/useDialog';
import { NadoWithdrawAmountStep } from './NadoWithdrawAmountStep';
import { ChainIcon } from 'client/modules/collateral/nadoDeposit/components/NadoChainSelect';
import { NadoTokenSelect } from 'client/modules/collateral/nadoDeposit/components/NadoTokenSelect';
import {
  ETHEREUM_DEPOSIT_CHAIN,
  DepositTokenSymbol,
  DEPOSIT_CHAINS,
} from 'client/modules/collateral/nadoDeposit/config/chains';
import { useNadoWithdrawForm } from '../hooks/useNadoWithdrawForm';
import { NadoWithdrawStep } from '../types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { joinClassNames } from '@vertex-protocol/web-common';
import { CardButton, Icons, UpDownChevronIcon } from '@vertex-protocol/web-ui';

export type NadoWithdrawDialogParams = Record<string, never>;

/**
 * Main entry point for the Nado-style withdrawal dialog.
 *
 * Token-first model: token is selected first, then the chain list is filtered
 * to those supporting that token.
 *
 * Two-step flow:
 *  1. Select token (USDT0 / USDC) then destination chain
 *  2. Enter amount and execute withdrawal + bridge
 *
 * Defaults: USDT0 token, Ethereum chain.
 */
export function NadoWithdrawDialog() {
  const { hide } = useDialog();

  const [step, setStep] = useState<NadoWithdrawStep>('selection');
  const [selectedTokenSymbol, setSelectedTokenSymbol] = useState<string>('USDT0');
  const [selectedChainId, setSelectedChainId] = useState<number>(
    ETHEREUM_DEPOSIT_CHAIN.chainId,
  );

  const { formData, formActions } = useNadoWithdrawForm({
    selectedChainId,
    selectedTokenSymbol,
  });

  function handleTokenSelected(tokenSymbol: string) {
    setSelectedTokenSymbol(tokenSymbol);
    const chains = getNadoWithdrawChainsForToken(tokenSymbol as DepositTokenSymbol);
    const preferred = chains.find((c) => c.chainId === ETHEREUM_DEPOSIT_CHAIN.chainId) ?? chains[0];
    if (preferred) {
      setSelectedChainId(preferred.chainId);
    }
  }

  function handleChainSelected(chainId: number) {
    setSelectedChainId(chainId);
  }

  function handleContinue() {
    setStep('amount');
  }

  function handleBack() {
    setStep('selection');
  }

  // Prevent closing while a transaction is in-flight
  const isTransacting = [
    'withdrawing', 'approving_oft', 'bridging', 'bridge_pending',
    'switching_chain',
  ].includes(formData.buttonState) || formData.isFundingAutomationActive;

  const handleClose = isTransacting ? undefined : hide;

  return (
    <BaseAppDialog.Container onClose={handleClose}>
      <BaseAppDialog.Title onClose={handleClose}>
        {step === 'amount' && !isTransacting && (
          <button
            onClick={handleBack}
            className="text-text-tertiary hover:text-text-secondary mr-1 transition-colors"
            aria-label="Go back"
          >
            ←
          </button>
        )}
        Withdraw
      </BaseAppDialog.Title>
      <BaseAppDialog.Body>
        {step === 'selection' ? (
          <WithdrawSelectionStep
            selectedTokenSymbol={selectedTokenSymbol}
            selectedChainId={selectedChainId}
            onTokenSelected={handleTokenSelected}
            onChainSelected={handleChainSelected}
            onContinue={handleContinue}
          />
        ) : (
          <NadoWithdrawAmountStep
            {...formData}
            {...formActions}
            onBack={handleBack}
          />
        )}
      </BaseAppDialog.Body>
    </BaseAppDialog.Container>
  );
}

interface SelectionStepProps {
  selectedTokenSymbol: string;
  selectedChainId: number;
  onTokenSelected: (tokenSymbol: string) => void;
  onChainSelected: (chainId: number) => void;
  onContinue: () => void;
}

/**
 * Token-first selection step for withdrawals.
 *
 * Layout:
 *  - Token dropdown (USDT0 / USDC)
 *  - Chain dropdown (filtered to chains supporting the selected token)
 *  - "Withdraw" card
 */
function WithdrawSelectionStep({
  selectedTokenSymbol,
  selectedChainId,
  onTokenSelected,
  onChainSelected,
  onContinue,
}: SelectionStepProps) {
  return (
    <div className="flex flex-col gap-y-3 min-h-[220px]">
      <NadoTokenSelect
        selectedTokenSymbol={selectedTokenSymbol}
        onTokenSelected={onTokenSelected}
      />
      <WithdrawChainSelect
        selectedChainId={selectedChainId}
        tokenSymbol={selectedTokenSymbol as DepositTokenSymbol}
        onChainSelected={onChainSelected}
      />
      <WithdrawToWalletCard onClick={onContinue} />
    </div>
  );
}

/**
 * Chain selector for withdrawals — only shows chains that support the selected
 * token and are not the primary Ink chain.
 */
function WithdrawChainSelect({
  selectedChainId,
  tokenSymbol,
  onChainSelected,
}: {
  selectedChainId: number;
  tokenSymbol: DepositTokenSymbol;
  onChainSelected: (chainId: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const chains = useMemo(
    () => getNadoWithdrawChainsForToken(tokenSymbol),
    [tokenSymbol],
  );

  const selectedChain = useMemo(
    () => chains.find((c) => c.chainId === selectedChainId),
    [chains, selectedChainId],
  );

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={joinClassNames(
          'bg-surface-1 hover:bg-surface-2 border-stroke',
          'flex w-full items-center justify-between rounded-sm border px-3 py-2.5',
          'transition-colors outline-none',
        )}
      >
        <div className="flex items-center gap-x-2.5">
          <ChainIcon chainId={selectedChain?.chainId} />
          <span className="text-text-primary text-sm">
            {selectedChain?.name ?? 'Select chain'}
          </span>
        </div>
        <UpDownChevronIcon open={open} className="text-text-tertiary size-3" />
      </button>

      {open && (
        <div
          className={joinClassNames(
            'bg-surface-2 border-stroke shadow-elevation-strong',
            'absolute left-0 right-0 top-full z-10 mt-1 rounded-sm border',
            'flex flex-col py-1',
          )}
        >
          {chains.map((chain) => (
            <button
              key={chain.chainId}
              type="button"
              onClick={() => {
                onChainSelected(chain.chainId);
                setOpen(false);
              }}
              className={joinClassNames(
                'flex items-center gap-x-2.5 px-3 py-2 text-left',
                'transition-colors hover:bg-overlay-hover',
                chain.chainId === selectedChainId
                  ? 'text-text-primary'
                  : 'text-text-secondary',
              )}
            >
              <ChainIcon chainId={chain.chainId} />
              <span className="text-sm">{chain.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function getNadoWithdrawChainsForToken(tokenSymbol: DepositTokenSymbol) {
  if (tokenSymbol === 'USDC' || tokenSymbol === 'USDT0') {
    return [ETHEREUM_DEPOSIT_CHAIN];
  }
  return DEPOSIT_CHAINS.filter((chain) => !chain.isPrimary);
}

function WithdrawToWalletCard({ onClick }: { onClick: () => void }) {
  return (
    <CardButton
      className={joinClassNames(
        'bg-surface-card hover:bg-surface-2',
        'flex items-center gap-x-3 rounded-lg px-4 py-3',
        'transition-colors',
      )}
      onClick={onClick}
    >
      <Icons.Wallet className="text-text-secondary size-5 flex-shrink-0" />
      <p className="text-text-primary flex-1 text-left text-sm font-medium">
        Withdraw
      </p>
      <Icons.CaretRight className="text-text-tertiary size-4 flex-shrink-0" />
    </CardButton>
  );
}
